import { Trans, t } from "@lingui/macro";
import {
  Box,
  Container,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useMemo } from "react";
import { useWorkoutProfilesContext } from "@/hooks/useWorkoutProfilesContext";
import {
  BlueSwitch,
  EducationTypography,
  MenuList,
  MenuListItemType,
  MenuListItemVariant,
} from "@WahooFitness/redesignr";
import { useSettingsRemoteConfig } from "@/hooks/useSettingsRemoteConfig";
import { ErrorBoundary, GlobalHeader } from "@WahooFitness/wahoo-offline-mfe";

const SummitSegmentSettings = ({ workoutProfileId }: { workoutProfileId: string }) => {
  const { palette } = useTheme();
  const { getSettingsRemoteConfigString } = useSettingsRemoteConfig();

  const climbSizeUrl = useMemo(
    () => getSettingsRemoteConfigString("SUMMIT_SEGMENT_SETTINGS_CLIMB_SIZE_URL"),
    [getSettingsRemoteConfigString]
  );

  const learnMoreUrl = useMemo(
    () => getSettingsRemoteConfigString("SUMMIT_SEGMENT_SETTINGS_LEARN_MORE_URL"),
    [getSettingsRemoteConfigString]
  );

  const {
    getWorkoutProfile,
    workoutProfilesLoading,
    workoutProfilesError,
    updateProfile,
    profileUpdateInProgress,
  } = useWorkoutProfilesContext();

  const workoutProfile = useMemo(
    () => getWorkoutProfile(workoutProfileId),
    [getWorkoutProfile, workoutProfileId]
  );

  const handleChangeClimbsSensitivity = useCallback(
    (_: any, value: number) => {
      if (value !== null && value !== undefined && workoutProfile) {
        updateProfile(workoutProfile.external_id, "climbs_sensitivity", value);
      }
    },
    [updateProfile, workoutProfile]
  );

  const firstSection = useMemo(() => {
    const menuListItems: MenuListItemType[] = [
      {
        id: "climbs_enabled",
        content: t`Enable Summit Segments`,
        variant: MenuListItemVariant.Action,
        disabled: profileUpdateInProgress,
        actionComponent: (
          <BlueSwitch
            disabled={profileUpdateInProgress}
            checked={workoutProfile?.climbs_enabled}
            data-testid="climbs_enabled"
          />
        ),
        action: () => {
          if (workoutProfile) {
            updateProfile(
              workoutProfile.external_id,
              "climbs_enabled",
              !workoutProfile.climbs_enabled
            );
          }
        },
      },
    ];
    if (!workoutProfile?.climbs_enabled) {
      return menuListItems;
    }
    menuListItems.push(
      ...[
        {
          id: "climbs_routes_only",
          content: t`Routes only`,
          secondaryContent: t`If enabled, you will only get Summit Segments when following a route.`,
          variant: MenuListItemVariant.Action,
          disabled: profileUpdateInProgress,
          actionComponent: (
            <BlueSwitch
              disabled={profileUpdateInProgress}
              checked={workoutProfile?.climbs_routes_only}
              data-testid="climbs_routes_only"
            />
          ),
          action: () => {
            if (workoutProfile) {
              updateProfile(
                workoutProfile.external_id,
                "climbs_routes_only",
                !workoutProfile.climbs_routes_only
              );
            }
          },
        },
        {
          id: "climbs_sensitivity",
          content: t`Climb Detection`,
          secondaryContent: (
            <Box display="flex" flexDirection="column" rowGap={1}>
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={workoutProfile?.climbs_sensitivity}
                onChange={handleChangeClimbsSensitivity}
                disabled={profileUpdateInProgress}
                data-testid="climbs_sensitivity"
                color="info"
              >
                <ToggleButton value={0}>
                  <Trans>All climbs</Trans>
                </ToggleButton>
                <ToggleButton value={1}>
                  <Trans>Med & Lg</Trans>
                </ToggleButton>
                <ToggleButton value={2}>
                  <Trans>Large</Trans>
                </ToggleButton>
              </ToggleButtonGroup>
              <Link href={climbSizeUrl} target="_blank" sx={{ textDecoration: "none" }}>
                <EducationTypography color={palette.info.main} variant="prose-sm">
                  <Trans>What determines ELEMNT&apos;s climb size?</Trans>
                </EducationTypography>
              </Link>
            </Box>
          ),
          disabled: profileUpdateInProgress,
          variant: MenuListItemVariant.Accordion,
          expanded: true,
          actionComponent: <></>,
        },
      ]
    );
    return menuListItems;
  }, [
    climbSizeUrl,
    handleChangeClimbsSensitivity,
    palette.info.main,
    profileUpdateInProgress,
    updateProfile,
    workoutProfile,
  ]);

  const secondSection = useMemo(
    () => [
      {
        id: "climbs_auto_page_change",
        content: t`Automatic page change`,
        secondaryContent: t`Will change to the Summit Segments Live page at the start of each climb.`,
        variant: MenuListItemVariant.Action,
        disabled: profileUpdateInProgress,
        actionComponent: (
          <BlueSwitch
            disabled={profileUpdateInProgress}
            checked={workoutProfile?.climbs_auto_page_change}
            data-testid="climbs_auto_page_change"
          />
        ),
        action: () => {
          if (workoutProfile) {
            updateProfile(
              workoutProfile.external_id,
              "climbs_auto_page_change",
              !workoutProfile.climbs_auto_page_change
            );
          }
        },
      },
      {
        id: "climbs_alerts_on_other_pages",
        content: t`Alerts on other pages`,
        secondaryContent: t`Keeps you updated on your climb progress while not on the Summit Segments Live page.`,
        variant: MenuListItemVariant.Action,
        disabled: profileUpdateInProgress,
        actionComponent: (
          <BlueSwitch
            disabled={profileUpdateInProgress}
            checked={workoutProfile?.climbs_alerts_on_other_pages}
            data-testid="climbs_alerts_on_other_pages"
          />
        ),
        action: () => {
          if (workoutProfile) {
            updateProfile(
              workoutProfile.external_id,
              "climbs_alerts_on_other_pages",
              !workoutProfile.climbs_alerts_on_other_pages
            );
          }
        },
      },
      {
        id: "climbs_during_plan",
        content: t`Enabled during planned workouts`,
        variant: MenuListItemVariant.Action,
        disabled: profileUpdateInProgress,
        actionComponent: (
          <BlueSwitch
            disabled={profileUpdateInProgress}
            checked={workoutProfile?.climbs_during_plan}
            data-testid="climbs_during_plan"
          />
        ),
        action: () => {
          if (workoutProfile) {
            updateProfile(
              workoutProfile.external_id,
              "climbs_during_plan",
              !workoutProfile.climbs_during_plan
            );
          }
        },
      },
      {
        id: "climbs_learn_more",
        content: t`Learn more`,
        variant: MenuListItemVariant.ExternalLink,
        linkLocation: learnMoreUrl,
      },
    ],
    [learnMoreUrl, profileUpdateInProgress, updateProfile, workoutProfile]
  );

  return (
    <>
      <GlobalHeader title={t`Summit Segments`} />
      <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
        <ErrorBoundary isLoading={workoutProfilesLoading} error={workoutProfilesError}>
          <Box py={2}>
            <Typography
              sx={{ px: 2, pb: 1, width: "100%", display: "block" }}
              align="center"
              variant="prose-sm"
              color={palette.text.secondary}
            >
              <Trans>Settings only apply to ELEMNTs that have Summit Segments.</Trans>
            </Typography>

            <Box width="100%">
              <MenuList listItems={firstSection} />
            </Box>
            {workoutProfile?.climbs_enabled && (
              <Box width="100%">
                <MenuList listItems={secondSection} />
              </Box>
            )}
          </Box>
        </ErrorBoundary>
      </Container>
    </>
  );
};

export default SummitSegmentSettings;
